import { ITEM_TYPE_CONFIG } from './commitmentConstants';

/** Replace {stakeholder} tokens for live display; keeps the literal if no name yet. */
export function fillStakeholder(text, stakeholder) {
  if (!text || !text.includes('{stakeholder}')) return text || '';
  if (!stakeholder?.trim()) return text;
  return text.replaceAll('{stakeholder}', stakeholder.trim()).replace(/\s+/g, ' ').trim();
}

/** deadline (yyyy-mm-dd) + offset days → yyyy-mm-dd, or null. */
export function computeOffsetDate(deadline, offset) {
  if (!deadline || offset == null) return null;
  // Pure UTC calendar arithmetic — `new Date('...T00:00:00')` is LOCAL midnight,
  // and .toISOString() then shifts it a day back for UTC+ zones like IST.
  const [y, m, d] = String(deadline).split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(dt.getTime())) return null;
  dt.setUTCDate(dt.getUTCDate() + Number(offset));
  return dt.toISOString().slice(0, 10);
}

const inputStyle = {
  background: 'var(--pd-panel-solid)',
  border: '1px solid var(--pd-border)',
  color: 'var(--pd-text)',
};

const DURATIONS = [15, 30, 45, 60, 90, 120];

function EventFields({ item, idx, update, deadline }) {
  const dateValue = item.date || computeOffsetDate(deadline, item.dueDateOffsetDays) || '';
  return (
    <div className="flex items-center gap-2">
      <input data-testid={`tpl-item-date-${idx}`} type="date" value={dateValue}
        onChange={(e) => update({ date: e.target.value })}
        className="px-2 py-1.5 rounded-lg text-[11px] outline-none" style={inputStyle} />
      <input data-testid={`tpl-item-start-${idx}`} type="time" value={item.startTime || '10:00'}
        onChange={(e) => update({ startTime: e.target.value })}
        className="px-2 py-1.5 rounded-lg text-[11px] outline-none" style={inputStyle} />
      <select data-testid={`tpl-item-duration-${idx}`} value={item.durationMinutes || 60}
        onChange={(e) => update({ durationMinutes: Number(e.target.value) })}
        className="px-2 py-1.5 rounded-lg text-[11px] outline-none" style={inputStyle}>
        {DURATIONS.map((m) => <option key={m} value={m}>{`${m} min`}</option>)}
      </select>
    </div>
  );
}

function TypeFields({ item, idx, update, stakeholder, deadline }) {
  switch (item.itemType) {
    case 'event':
      return <EventFields item={item} idx={idx} update={update} deadline={deadline} />;
    case 'call':
      return (
        <input data-testid={`tpl-item-contact-${idx}`} value={fillStakeholder(item.contactName, stakeholder)}
          onChange={(e) => update({ contactName: e.target.value })}
          placeholder="Who to call (e.g., Shawn)"
          className="w-full px-2 py-1.5 rounded-lg text-[11px] outline-none" style={inputStyle} />
      );
    case 'reading':
      return (
        <input data-testid={`tpl-item-url-${idx}`} value={item.url || ''}
          onChange={(e) => update({ url: e.target.value })}
          placeholder="https:// (optional link to the document)"
          className="w-full px-2 py-1.5 rounded-lg text-[11px] outline-none" style={inputStyle} />
      );
    case 'payment':
      return (
        <div className="flex items-center gap-2">
          <input data-testid={`tpl-item-amount-${idx}`} type="number" min="0" value={item.amount ?? ''}
            onChange={(e) => update({ amount: e.target.value === '' ? 0 : Number(e.target.value) })}
            placeholder="Amount" className="w-28 px-2 py-1.5 rounded-lg text-[11px] outline-none" style={inputStyle} />
          <span className="text-[10px]" style={{ color: 'var(--pd-subtle)' }}>{item.currency || 'INR'}</span>
        </div>
      );
    case 'note':
      return (
        <input data-testid={`tpl-item-desc-${idx}`} value={fillStakeholder(item.description, stakeholder)}
          onChange={(e) => update({ description: e.target.value })}
          placeholder="What to capture in this note"
          className="w-full px-2 py-1.5 rounded-lg text-[11px] outline-none" style={inputStyle} />
      );
    case 'task': {
      const due = computeOffsetDate(deadline, item.dueDateOffsetDays);
      return (
        <div className="flex items-center gap-2 text-[10px]" style={{ color: 'var(--pd-subtle)' }}>
          {item.priority && <span className="font-semibold uppercase">{item.priority}</span>}
          {item.estimatedMinutes ? <span>{item.estimatedMinutes}m</span> : null}
          {due && <span>due {due}</span>}
        </div>
      );
    }
    default:
      return null;
  }
}

/**
 * Fill-in editor for template items: each blueprinted item is editable
 * (title + type-specific fields) and can be excluded before creation.
 * `{stakeholder}` placeholders substitute live as the stakeholder is typed.
 */
export default function TemplateFillInForm({ items, onChange, stakeholder, deadline }) {
  const update = (idx, patch) => {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  return (
    <div className="rounded-xl px-3 py-2.5" style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)' }}>
      <div className="text-[9px] uppercase tracking-wider font-bold mb-2" style={{ color: 'var(--pd-subtle)' }}>
        Fill in the items to create
      </div>
      <div className="space-y-2.5 max-h-[300px] overflow-y-auto custom-scrollbar pr-1">
        {items.map((it, idx) => {
          const cfg = ITEM_TYPE_CONFIG[it.itemType] || ITEM_TYPE_CONFIG.task;
          const stableKey = it.id || `${it.itemType}-${(it.title || '').slice(0, 24)}-${idx}`;
          return (
            <div key={stableKey} data-testid={`tpl-item-row-${idx}`} className="rounded-lg px-2 py-2"
              style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)', opacity: it.include ? 1 : 0.45 }}>
              <div className="flex items-center gap-2">
                <input data-testid={`tpl-item-include-${idx}`} type="checkbox" checked={!!it.include}
                  onChange={(e) => update(idx, { include: e.target.checked })}
                  className="w-3.5 h-3.5 flex-shrink-0 accent-current cursor-pointer"
                  style={{ color: cfg.color }} />
                <span className="flex items-center gap-1 text-[8px] uppercase font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                  style={{ background: `${cfg.color}12`, color: cfg.color }}>
                  <cfg.icon className="w-2.5 h-2.5" />{cfg.label}
                </span>
                <input data-testid={`tpl-item-title-${idx}`} value={fillStakeholder(it.title, stakeholder)}
                  onChange={(e) => update(idx, { title: e.target.value })}
                  disabled={!it.include}
                  className="flex-1 min-w-0 px-2 py-1.5 rounded-lg text-[11px] font-medium outline-none"
                  style={inputStyle} />
              </div>
              {it.include && (
                <div className="mt-1.5 pl-6">
                  <TypeFields item={it} idx={idx} update={(patch) => update(idx, patch)}
                    stakeholder={stakeholder} deadline={deadline} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
